import { AloftArea, schMainPropertyType } from '@aloft/toolkit-shared';
import {
  Block,
  Button,
  Checkbox,
  Flex,
  SimpleInput,
  SimpleSelect,
  Typography,
} from '@aloft/toolkit-sui';

import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { z } from 'zod';

import { useRunRegressionAPI } from '../../../hooks/api/toolkitComponents.ts';
import { useZodForm } from '../../../hooks/useZodForm.ts';
import { AreaInput } from '../../analysis/InputComponents/AreaInput.tsx';
import { NumberInput } from '../../analysis/InputComponents/NumberInput.tsx';
import { RegressionFilters } from './RegressionFilters.tsx';
import { RegressionMap } from './RegressionMap.tsx';
import { RegressionModelOutput } from './RegressionModelOutput.tsx';
import { RegressionPriceHistogram } from './RegressionPriceHistogram.tsx';

const schRegressionForm = z.object({
  subject: z.object({
    zipCode: z.string().min(5).max(5),
    propertyType: schMainPropertyType,
    gla: z.number().int().positive(),
    bedrooms: z.number().int().nonnegative(),
    fullBaths: z.number().int().nonnegative(),
    halfBaths: z.number().int().nonnegative(),
    siteSize: z.number().int().positive(),
    parkingSpaces: z.number().int().nonnegative(),
    actualAge: z.number().int().nonnegative(),
  }),
  dropCollinearFeatures: z.boolean().default(true),
});

type RegressionFormValues = z.infer<typeof schRegressionForm>;

export const RegresssionModelling = () => {
  const { mutate, isPending, data } = useRunRegressionAPI();

  const form = useZodForm({
    schema: schRegressionForm,
    defaultValues: {
      subject: {
        zipCode: '',
        propertyType: 'singleFamilyResidence' as const,
        gla: undefined,
        bedrooms: undefined,
        fullBaths: undefined,
        halfBaths: 0,
        siteSize: undefined,
        parkingSpaces: 0,
        actualAge: 0,
      },
      dropCollinearFeatures: true,
    },
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  const { control, formState, handleSubmit, watch } = form;
  const { errors } = formState;

  const lastSubmittedValues = useRef<RegressionFormValues | null>(null);
  const currentValues = watch();
  const isUnchanged =
    lastSubmittedValues.current != null &&
    JSON.stringify(currentValues) === JSON.stringify(lastSubmittedValues.current);

  const onSubmit = (values: RegressionFormValues) => {
    lastSubmittedValues.current = values;
    mutate({
      body: {
        subject: {
          propertyType: values.subject.propertyType,
          gla: AloftArea.fromNumber(values.subject.gla, 'sqft').toJSON(),
          bedrooms: values.subject.bedrooms,
          fullBaths: values.subject.fullBaths,
          halfBaths: values.subject.halfBaths,
          siteSize: AloftArea.fromNumber(values.subject.siteSize, 'sqft').toJSON(),
          parkingSpaces: values.subject.parkingSpaces,
          actualAge: values.subject.actualAge,
        },
        zipCode: values.subject.zipCode,
        dropCollinearFeatures: values.dropCollinearFeatures,
      },
    });
  };

  return (
    <Flex flexDirection="column" padding="32px" gap="32px">
      <Typography variant="h2">Unit Rates OLS Regression</Typography>

      <Flex flexDirection="column" gap="24px">
        {/* Subject Property */}
        <Flex flexDirection="column" gap="12px">
          <Typography variant="h4">Reference Property</Typography>

          <Flex flexWrap="wrap" gap="12px" alignItems="flex-end">
            <Controller
              name="subject.zipCode"
              control={control}
              render={({ field }) => (
                <SimpleInput
                  {...field}
                  type="text"
                  size="sm"
                  label="ZIP Code"
                  required
                  invalid={!!errors.subject?.zipCode}
                  errorMessage={errors.subject?.zipCode?.message}
                  width="120px"
                />
              )}
            />
            <Controller
              name="subject.propertyType"
              control={control}
              render={({ field }) => (
                <SimpleSelect
                  {...field}
                  size="sm"
                  label="Property Type"
                  required
                  invalid={!!errors.subject?.propertyType}
                  errorMessage={errors.subject?.propertyType?.message}
                  width="200px"
                >
                  <option value="singleFamilyResidence">Single Family</option>
                  <option value="townhouse">Townhouse</option>
                  <option value="manufacturedHome">Manufactured Home</option>
                  <option value="multiFamily">Multi-Family</option>
                </SimpleSelect>
              )}
            />
          </Flex>
          <Flex flexWrap="wrap" gap="12px">
            <Controller
              name="subject.gla"
              control={control}
              render={({ field }) => (
                <Block width="120px">
                  <AreaInput
                    {...field}
                    value={field.value?.toString()}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                    }
                    label="GLA"
                    required
                    errorMessage={errors.subject?.gla?.message}
                  />
                </Block>
              )}
            />
            <Controller
              name="subject.siteSize"
              control={control}
              render={({ field }) => (
                <Block width="120px">
                  <AreaInput
                    {...field}
                    value={field.value?.toString()}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                    }
                    label="Site Size"
                    required
                    errorMessage={errors.subject?.siteSize?.message}
                  />
                </Block>
              )}
            />
            <Controller
              name="subject.bedrooms"
              control={control}
              render={({ field }) => (
                <Block width="100px">
                  <NumberInput
                    {...field}
                    value={field.value?.toString()}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    label="Bedrooms"
                    min={0}
                    required
                    errorMessage={errors.subject?.bedrooms?.message}
                  />
                </Block>
              )}
            />
            <Controller
              name="subject.fullBaths"
              control={control}
              render={({ field }) => (
                <Block width="100px">
                  <NumberInput
                    {...field}
                    value={field.value?.toString()}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    label="Full Baths"
                    min={0}
                    required
                    errorMessage={errors.subject?.fullBaths?.message}
                  />
                </Block>
              )}
            />
            <Controller
              name="subject.halfBaths"
              control={control}
              render={({ field }) => (
                <Block width="100px">
                  <NumberInput
                    {...field}
                    value={field.value?.toString()}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    label="Half Baths"
                    min={0}
                    required
                    errorMessage={errors.subject?.halfBaths?.message}
                  />
                </Block>
              )}
            />
            <Controller
              name="subject.parkingSpaces"
              control={control}
              render={({ field }) => (
                <Block width="120px">
                  <NumberInput
                    {...field}
                    value={field.value?.toString()}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    label="Parking Spaces"
                    min={0}
                    required
                    errorMessage={errors.subject?.parkingSpaces?.message}
                  />
                </Block>
              )}
            />
            <Controller
              name="subject.actualAge"
              control={control}
              render={({ field }) => (
                <Block width="100px">
                  <NumberInput
                    {...field}
                    value={field.value?.toString()}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    label="Age (years)"
                    min={0}
                    required
                    errorMessage={errors.subject?.actualAge?.message}
                  />
                </Block>
              )}
            />
          </Flex>
        </Flex>

        {/* Settings */}
        <Flex flexDirection="column" gap="12px">
          <Typography variant="h4">Settings</Typography>
          <Flex flexWrap="wrap" gap="12px" alignItems="flex-end">
            <Controller
              name="dropCollinearFeatures"
              control={control}
              render={({ field }) => (
                <Checkbox
                  isChecked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                >
                  Drop collinear features
                </Checkbox>
              )}
            />
          </Flex>
        </Flex>

        <Button
          text="Run Regression"
          isLoading={isPending}
          disabled={isPending || isUnchanged}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={() => handleSubmit(onSubmit)()}
        />
      </Flex>

      {/* Results */}
      {data && (
        <Flex flexDirection="column" gap="32px">
          {data.status === 'error' ? (
            <Block
              backgroundColor="alertErrorLight"
              borderColor="alertError"
              border="1px solid"
              borderRadius="6px"
              padding="16px"
            >
              <Flex flexDirection="column" gap="4px">
                <Typography variant="h4" color="alertError">
                  Error
                </Typography>
                <Typography variant="body2" color="textError">
                  {data.message}
                </Typography>
              </Flex>
            </Block>
          ) : (
            <>
              <RegressionModelOutput
                constant={data.output.constant}
                unitRates={data.output.unitRates}
                modelDiagnostics={data.output.modelDiagnostics}
              />
              <RegressionFilters
                filters={data.output.searchCriteria.filters}
                filterTier={data.output.filterTier}
              />
              <RegressionMap listings={data.output.listings} />
              <RegressionPriceHistogram listings={data.output.listings} />
            </>
          )}
        </Flex>
      )}
    </Flex>
  );
};
